import { cloneDeep } from 'lodash';

import {
  getOrCreateProgressForQuiz,
  LearningProgressStatus,
  ProgressForCourses,
  ProgressForLessons,
  saveProgressForLesson,
  saveProgressForQuiz,
} from 'src/services/http/progress';
import { ContentfulLearningPathWithNavigation } from 'src/graphql/queries/GetLearningPathWithNavigation';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { ContentfulSmLesson } from 'src/graphql/queries/GetSmLesson';
import { User } from 'src/types';

export const calculateLearningPathCompletionRate = (
  progress?: ProgressForCourses,
  learningPath?: ContentfulLearningPathWithNavigation,
): number => {
  if (!progress || !learningPath) {
    return 0;
  }

  const numberOfCompletedCourses = progress.courses.filter(
    (p) =>
      learningPath.coursesCollection.items.find((course) => course.sys.id === p.courseId) &&
      p.status === LearningProgressStatus.COMPLETED,
  ).length;

  return (numberOfCompletedCourses / learningPath.coursesCollection.items.length) * 100;
};

export const calculateCourseCompletionRate = (
  progress: ProgressForLessons | undefined,
  course: ContentfulSmCourse | undefined,
): number => {
  if (!progress || !course) {
    return 0;
  }

  const numberOfCompletedLessons = progress.lessons.filter(
    (p) =>
      course.lessonsCollection.items.find((lesson) => lesson.sys.id === p.lessonId) &&
      p.status === LearningProgressStatus.COMPLETED,
  ).length;

  return (numberOfCompletedLessons / course.lessonsCollection.items.length) * 100;
};

export const completeSectionAsync = (
  progress: ProgressForLessons | undefined,
  section: string,
  lesson: ContentfulSmLesson,
  user: User | null | undefined,
  preview: boolean,
): ProgressForLessons => {
  const nextProgress = cloneDeep(progress || {}) as ProgressForLessons;

  if (!nextProgress.lessons) {
    nextProgress.lessons = [];
  }

  let lessonIndex = nextProgress.lessons.findIndex((p) => p.lessonId === lesson.sys.id);

  if (lessonIndex < 0) {
    nextProgress.lessons.push({
      lessonId: lesson.sys.id,
      status: LearningProgressStatus.IN_PROGRESS,
      section: [],
    });
    lessonIndex = nextProgress.lessons.length - 1;
  }

  const sectionIndex = nextProgress.lessons[lessonIndex].section.findIndex((s) => s.sectionId === section);

  if (sectionIndex < 0) {
    nextProgress.lessons[lessonIndex].section.push({
      sectionId: section,
      status: LearningProgressStatus.COMPLETED,
    });
  } else {
    nextProgress.lessons[lessonIndex].section[sectionIndex].status = LearningProgressStatus.COMPLETED;
  }

  if (user && !preview) {
    saveProgressForLesson({
      atlassianId: user.account_id,
      lesson: {
        lessonId: lesson.sys.id,
        status: LearningProgressStatus.IN_PROGRESS,
        section: {
          sectionId: section,
          status: LearningProgressStatus.COMPLETED,
        },
      },
    });
  }

  return nextProgress;
};

export const completeLessonAsync = async (
  user: User | null | undefined,
  lesson: ContentfulSmLesson | undefined,
  progress: ProgressForLessons | undefined,
): Promise<void> => {
  if (!lesson || !user) {
    return;
  }

  // complete any leftover sections
  const currentLessonProgress = progress?.lessons.find((p) => p.lessonId === lesson.sys.id);

  lesson.sectionListCollection.items.forEach((section) => {
    const currentSectionProgress = currentLessonProgress?.section?.find((p) => p.sectionId === section.id);

    if (currentSectionProgress?.status !== LearningProgressStatus.COMPLETED) {
      saveProgressForLesson({
        atlassianId: user.account_id,
        lesson: {
          lessonId: lesson.sys.id,
          status: LearningProgressStatus.COMPLETED,
          section: {
            sectionId: section.sys.id,
            status: LearningProgressStatus.COMPLETED,
          },
        },
      });
    }
  });

  // obviously, it is an SQS call, so no guaranty progress for lesson
  // will be created earlier than progress for quiz (in case lesson has no sections)
  // but let's at least try
  await saveProgressForLesson({
    atlassianId: user.account_id,
    lesson: {
      lessonId: lesson.sys.id,
      status: LearningProgressStatus.COMPLETED,
    },
  });

  // it is important to ensure that progress for quiz was created before updating it
  await getOrCreateProgressForQuiz(lesson.sys.id, lesson.quiz.sys.id);

  saveProgressForQuiz({
    atlassianId: user.account_id,
    lessonId: lesson.sys.id,
    quizId: lesson.quiz.sys.id,
    newStatus: LearningProgressStatus.COMPLETED,
  });
};

export const updateProgressUponLessonCompletion = (
  course: ContentfulSmCourse | undefined,
  lesson: ContentfulSmLesson,
  progress: ProgressForLessons | undefined,
): ProgressForLessons => {
  const areAllLessonsCompleted = course
    ? course.lessonsCollection.items.every((l) => !!progress?.lessons?.find((p) => p.lessonId === l.sys.id))
    : true; // if it is a standalone lesson, we don't care about course progress

  const nextProgress = {
    ...progress,
    lessons: cloneDeep(progress?.lessons || []),
    overallStatus: areAllLessonsCompleted ? LearningProgressStatus.COMPLETED : LearningProgressStatus.IN_PROGRESS,
  };

  const lessonProgress = nextProgress.lessons.find((p) => p.lessonId === lesson.sys.id);

  if (!lessonProgress) {
    nextProgress.lessons.push({
      lessonId: lesson.sys.id,
      status: LearningProgressStatus.COMPLETED,
      section: [
        {
          sectionId: 'quiz',
          status: LearningProgressStatus.COMPLETED,
        },
      ],
    });

    return nextProgress;
  }

  lessonProgress.status = LearningProgressStatus.COMPLETED;
  const sectionProgress = lessonProgress.section.find((p) => p.sectionId === 'quiz');

  if (!sectionProgress) {
    lessonProgress.section.push({
      sectionId: 'quiz',
      status: LearningProgressStatus.COMPLETED,
    });

    return nextProgress;
  }

  sectionProgress.status = LearningProgressStatus.COMPLETED;

  return nextProgress;
};

export const updateLearningPathProgressUponLessonCompletion = (
  course: ContentfulSmCourse,
  progressForLessons: ProgressForLessons | undefined,
  progressForCourses: ProgressForCourses | undefined,
): ProgressForCourses => {
  const nextProgressForCourses = {
    overallStatus: LearningProgressStatus.IN_PROGRESS,
    courses: [],
    ...progressForCourses,
  };

  if (!progressForLessons?.lessons.every((l) => l.status === LearningProgressStatus.COMPLETED)) {
    return nextProgressForCourses;
  }

  const courseProgressIndex = nextProgressForCourses.courses.findIndex((c) => c.courseId === course.sys.id);

  if (courseProgressIndex < 0) {
    nextProgressForCourses.courses.push({
      courseId: course.sys.id,
      status: LearningProgressStatus.COMPLETED,
    });
  } else if (nextProgressForCourses.courses[courseProgressIndex].status !== LearningProgressStatus.COMPLETED) {
    nextProgressForCourses.courses[courseProgressIndex].status = LearningProgressStatus.COMPLETED;
  }

  if (nextProgressForCourses.courses.every((c) => c.status === LearningProgressStatus.COMPLETED)) {
    nextProgressForCourses.overallStatus = LearningProgressStatus.COMPLETED;
  }

  return nextProgressForCourses;
};

export const updateLessonProgressUponLessonStart = (
  progressForLessons: ProgressForLessons,
  lessonId: string,
): ProgressForLessons => ({
  ...progressForLessons,
  lessons: progressForLessons.lessons.map((p) =>
    p.lessonId === lessonId
      ? {
        ...p,
        status: LearningProgressStatus.IN_PROGRESS,
      }
      : p,
  ),
});
